/**
 * 全局参数管理模块
 * 管理年份、亚型、半球等全局参数的状态变化
 */
import { getDefaultYear, getDefaultSubtype, getDefaultHemisphere } from '@/utils/dateUtils'

let state = () => ({
  // 当前选中的年份
  selectedYear: localStorage.getItem('selectedYear') || getDefaultYear(),
  // 当前选中的亚型
  selectedSubtype: localStorage.getItem('selectedSubtype') || getDefaultSubtype(),
  // 当前选中的半球/地区
  selectedRegion: localStorage.getItem('selectedRegion') || getDefaultHemisphere(),
  // 参数变化监听器列表
  paramChangeListeners: [],
  // 数据加载状态
  isDataLoading: false
})

let getters = {
  /**
   * 获取当前路径参数对象
   * @param {object} state - 状态对象
   * @returns {object} 包含year, subtype, region的参数对象
   */
  currentParams: (state) => {
    return {
      year: state.selectedYear,
      subtype: state.selectedSubtype,
      region: state.selectedRegion
    }
  },

  /**
   * 检查参数是否有变化
   * @param {object} state - 状态对象
   * @returns {function} 比较函数
   */
  hasParamsChanged: (state) => (newParams) => {
    return state.selectedYear !== newParams.year ||
      state.selectedSubtype !== newParams.subtype ||
      state.selectedRegion !== newParams.region
  }
}

let actions = {
  /**
   * 更新年份参数
   * @param {object} context - Vuex上下文
   * @param {string} year - 新的年份值
   */
  async updateYear({ commit, dispatch }, year) {
    commit('setYear', year)
    localStorage.setItem('selectedYear', year)
    await dispatch('notifyParamChange', { type: 'year', value: year })
  },

  /**
   * 更新亚型参数
   * @param {object} context - Vuex上下文
   * @param {string} subtype - 新的亚型值
   */
  async updateSubtype({ commit, dispatch }, subtype) {
    commit('setSubtype', subtype)
    localStorage.setItem('selectedSubtype', subtype)
    await dispatch('notifyParamChange', { type: 'subtype', value: subtype })
  },

  /**
   * 更新半球/地区参数
   * @param {object} context - Vuex上下文
   * @param {string} region - 新的半球/地区值
   */
  async updateRegion({ commit, dispatch }, region) {
    commit('setRegion', region)
    localStorage.setItem('selectedRegion', region)
    await dispatch('notifyParamChange', { type: 'region', value: region })
  },

  /**
   * 批量更新参数
   * @param {object} context - Vuex上下文
   * @param {object} params - 参数对象 {year, subtype, region}
   */
  async updateParams({ commit, dispatch }, params) {
    if (params.year) {
      commit('setYear', params.year)
      localStorage.setItem('selectedYear', params.year)
    }
    if (params.subtype) {
      commit('setSubtype', params.subtype)
      localStorage.setItem('selectedSubtype', params.subtype)
    }
    if (params.region) {
      commit('setRegion', params.region)
      localStorage.setItem('selectedRegion', params.region)
    }
    await dispatch('notifyParamChange', { type: 'batch', value: params })
  },

  /**
   * 注册参数变化监听器
   * @param {object} context - Vuex上下文
   * @param {function} listener - 监听器函数
   */
  registerParamListener({ commit }, listener) {
    commit('addParamListener', listener)
  },

  /**
   * 移除参数变化监听器
   * @param {object} context - Vuex上下文
   * @param {function} listener - 要移除的监听器函数
   */
  unregisterParamListener({ commit }, listener) {
    commit('removeParamListener', listener)
  },

  /**
   * 通知参数变化
   * @param {object} context - Vuex上下文
   * @param {object} changeInfo - 变化信息 {type, value}
   */
  async notifyParamChange({ state, commit }, changeInfo) {
    commit('setDataLoading', true)

    // 通知所有监听器
    for (let listener of state.paramChangeListeners) {
      try {
        // 再次检查listener是否为函数类型
        if (typeof listener === 'function') {
          await listener(changeInfo, state)
        } else {
          console.error('监听器不是函数类型，跳过执行:', typeof listener)
        }
      } catch (error) {
        console.error('参数变化监听器执行错误:', error)
      }
    }

    commit('setDataLoading', false)
  }
}

let mutations = {
  /**
   * 设置年份
   * @param {object} state - 状态对象
   * @param {string} year - 年份值
   */
  setYear(state, year) {
    state.selectedYear = year
  },

  /**
   * 设置亚型
   * @param {object} state - 状态对象
   * @param {string} subtype - 亚型值
   */
  setSubtype(state, subtype) {
    state.selectedSubtype = subtype
  },

  /**
   * 设置半球/地区
   * @param {object} state - 状态对象
   * @param {string} region - 半球/地区值
   */
  setRegion(state, region) {
    state.selectedRegion = region
  },

  /**
   * 添加参数监听器
   * @param {object} state - 状态对象
   * @param {function} listener - 监听器函数
   */
  addParamListener(state, listener) {
    // 检查listener是否为函数类型
    if (typeof listener !== 'function') {
      console.error('参数监听器必须是函数类型:', typeof listener)
      return
    }
    if (!state.paramChangeListeners.includes(listener)) {
      state.paramChangeListeners.push(listener)
    }
  },

  /**
   * 移除参数监听器
   * @param {object} state - 状态对象
   * @param {function} listener - 要移除的监听器函数
   */
  removeParamListener(state, listener) {
    let index = state.paramChangeListeners.indexOf(listener)
    if (index > -1) {
      state.paramChangeListeners.splice(index, 1)
    }
  },

  /**
   * 设置数据加载状态
   * @param {object} state - 状态对象
   * @param {boolean} loading - 加载状态
   */
  setDataLoading(state, loading) {
    state.isDataLoading = loading
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}